import { useMemo, useState } from 'react';
import { Pressable, ScrollView } from 'react-native';
import { Text, XStack, YStack } from 'tamagui';
import { AdaptivePanel, SearchField, useThemeColors, Icon } from '@kiips/ui';
import { ISO_4217_CURRENCIES } from '@my-fullstack-app/itinerary-types';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { createDisplayNameLookup } from '../../lib/intl-display-names';

/** Pinned shortcuts shown before the user searches. */
const COMMON = ['USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'CNY', 'INR', 'MXN', 'BRL', 'SGD'];

const ALL_CODES = [...ISO_4217_CURRENCIES].sort();

// Degrades to code-only (search still works by code) when Intl.DisplayNames
// is unavailable. See createDisplayNameLookup.
const currencyName = createDisplayNameLookup('currency');

interface CurrencyPickerProps {
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean | undefined;
  /** `field` (default): a bordered tappable field for forms. `inline`:
   *  a minimal code + chevron with no background, for dense rows
   *  (e.g. the cost-item editor) alongside `variant="inline"` inputs. */
  variant?: 'field' | 'inline';
}

/**
 * Searchable ISO 4217 currency picker. The trigger shows the current code
 * (+ name when the platform's Intl can resolve it); tapping opens a
 * searchable sheet with common currencies pinned. Replaces free-text
 * currency entry across the app (wishlist cost, cost-item editor,
 * preferred-currency setting, vault document amounts).
 */
export function CurrencyPicker({ value, onChange, disabled, variant = 'field' }: CurrencyPickerProps) {
  const theme = useThemeColors();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query, 150);
  const inline = variant === 'inline';

  const results = useMemo(() => {
    const q = debounced.trim().toUpperCase();
    if (!q) return COMMON.filter((c) => ISO_4217_CURRENCIES.has(c));
    return ALL_CODES.filter(
      (c) => c.includes(q) || (currencyName(c)?.toUpperCase().includes(q) ?? false),
    ).slice(0, 60);
  }, [debounced]);

  const name = currencyName(value);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  return (
    <>
      <Pressable
        onPress={() => !disabled && setOpen(true)}
        disabled={disabled}
        accessibilityLabel={`Currency: ${value}${name ? `, ${name}` : ''}. Tap to change.`}
        style={({ pressed }) => [
          inline
            ? { flexDirection: 'row', alignItems: 'center', gap: 2 }
            : {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                height: 44,
                paddingHorizontal: 12,
                borderRadius: 12,
                backgroundColor: theme.surfaceCanvas,
              },
          pressed && !disabled ? { opacity: 0.6 } : null,
        ]}
      >
        <Text fontSize={inline ? 13 : 15} fontWeight="700" color={theme.textOnSurface}>{value || '—'}</Text>
        <Icon name="expand-more" size={inline ? 16 : 18} color={theme.textSubdued} />
      </Pressable>

      <AdaptivePanel
        open={open}
        onOpenChange={(o) => { if (!o) close(); }}
        title="Currency"
        snapPoints={[70]}
      >
        <YStack paddingHorizontal={20} paddingTop={6} gap={12}>
          <SearchField
            value={query}
            onChangeText={setQuery}
            placeholder="Search name or code…"
            autoFocus
            accessibilityLabel="Search currency"
          />
          {!debounced.trim() ? (
            <Text fontSize={11} color="$textSubdued" fontWeight="700" letterSpacing={0.4}>COMMON</Text>
          ) : null}
          <ScrollView keyboardShouldPersistTaps="handled">
            {results.map((code) => {
              const cn = currencyName(code);
              const active = code === value;
              return (
                <Pressable
                  key={code}
                  onPress={() => { onChange(code); close(); }}
                  accessibilityLabel={`Select ${code}${cn ? ` ${cn}` : ''}`}
                  accessibilityState={{ selected: active }}
                  style={({ pressed }) => [
                    {
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 12,
                      paddingVertical: 12,
                      paddingHorizontal: 4,
                    },
                    pressed ? { opacity: 0.6 } : null,
                  ]}
                >
                  <Text fontSize={15} fontWeight="700" color="$textOnSurface" width={48}>{code}</Text>
                  {cn ? (
                    <Text fontSize={13} color="$textSubdued" flex={1} numberOfLines={1}>{cn}</Text>
                  ) : (
                    <YStack flex={1} />
                  )}
                  {active ? <Icon name="check" size={18} color={theme.textOnSurface} /> : null}
                </Pressable>
              );
            })}
            {results.length === 0 ? (
              <XStack paddingVertical={24} justifyContent="center">
                <Text fontSize={13} color="$textSubdued">No matching currency.</Text>
              </XStack>
            ) : null}
          </ScrollView>
        </YStack>
      </AdaptivePanel>
    </>
  );
}
